<script setup lang="ts">
/**
 * Where a document comes from, or goes to: one dialog for Open and Save As in
 * every document application. The rail lists the places — recently used files
 * (Open only), then the sources the host offers, with this computer before
 * Drive — and the pane says what picking one does.
 *
 * The dialog does not browse anything itself. A source hands over to its own
 * picker (the shell's Drive picker, the system file dialog), so `pick` and
 * `open-recent` report the choice, close the dialog, and the host takes over.
 */
import { computed, ref, watch } from 'vue';
import Clock from 'lucide-vue-next/dist/esm/icons/clock.js'; import Cloud from 'lucide-vue-next/dist/esm/icons/cloud.js'; import FileText from 'lucide-vue-next/dist/esm/icons/file-text.js'; import Monitor from 'lucide-vue-next/dist/esm/icons/monitor.js';
import NxButton from '../Button/NxButton.vue';
import NxModal from '../Modal/NxModal.vue';

export interface NxOfficeFileSource {
  readonly id: string;
  readonly label: string;
  readonly icon: 'drive' | 'computer';
  /** One sentence on what this place is. */
  readonly hint: string;
  /** The button that hands over to this source's own picker. */
  readonly actionLabel: string;
}

export interface NxOfficeRecentFile {
  readonly id: string;
  readonly name: string;
  /** Where and when, e.g. "Drive · docs/letter.writez · 12.09.2026". */
  readonly detail?: string;
  /** The file that is open already. */
  readonly current?: boolean;
}

const RECENT = 'recent';

const props = withDefaults(
  defineProps<{
    title: string;
    sourcesLabel: string;
    sources: readonly NxOfficeFileSource[];
    /** Recently used files; leave out for a dialog without that place (Save As). */
    recent?: readonly NxOfficeRecentFile[];
    recentLabel?: string;
    recentEmptyLabel?: string;
  }>(),
  { recent: undefined, recentLabel: 'Recent', recentEmptyLabel: 'No recent files' }
);
const open = defineModel<boolean>('open', { required: true });
const emit = defineEmits<{ pick: [sourceId: string]; 'open-recent': [id: string] }>();

const orderedSources = computed(() => [
  ...props.sources.filter((source) => source.icon === 'computer'),
  ...props.sources.filter((source) => source.icon !== 'computer'),
]);
const places = computed(() => [
  ...(props.recent ? [{ id: RECENT, label: props.recentLabel, icon: 'recent' as const }] : []),
  ...orderedSources.value,
]);
const active = ref<string>(RECENT);
const activeSource = computed(() => props.sources.find((source) => source.id === active.value));
// Save As starts on its primary computer action; Open keeps native modal focus.
const focusComputerAction = computed(
  () => props.recent === undefined && activeSource.value?.icon === 'computer'
);
const railRef = ref<HTMLElement | null>(null);

// Every visit starts on this computer when available, including when there
// are recent files. Hosts without a computer source keep their existing default.
watch(
  open,
  (isOpen) => {
    if (!isOpen) return;
    const somethingToReopen = props.recent?.some((file) => !file.current);
    active.value =
      props.sources.find((source) => source.icon === 'computer')?.id ??
      (somethingToReopen ? RECENT : (props.sources[0]?.id ?? RECENT));
  },
  { immediate: true }
);

function onRailKeydown(event: KeyboardEvent): void {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
  const ids = places.value.map((place) => place.id);
  const index = ids.indexOf(active.value);
  const next =
    event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? ids.length - 1
        : (index + (event.key === 'ArrowDown' ? 1 : -1) + ids.length) % ids.length;
  event.preventDefault();
  active.value = ids[next]!;
  railRef.value?.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
}

// The choice is reported before the dialog closes: a host that derives what
// the dialog is for from its own state still knows it when the choice arrives.
function pick(sourceId: string): void {
  emit('pick', sourceId);
  open.value = false;
}

function openRecent(file: NxOfficeRecentFile): void {
  if (file.current) return;
  emit('open-recent', file.id);
  open.value = false;
}
</script>

<template>
  <NxModal v-model:open="open" :autofocus="focusComputerAction">
    <template #header>{{ title }}</template>
    <template #body>
      <div class="nx-office-file-dialog">
        <div
          ref="railRef"
          class="nx-office-file-dialog__rail"
          role="tablist"
          aria-orientation="vertical"
          :aria-label="sourcesLabel"
          @keydown="onRailKeydown"
        >
          <button
            v-for="place in places"
            :key="place.id"
            type="button"
            role="tab"
            class="nx-office-file-dialog__place"
            :class="{ 'is-active': active === place.id }"
            :aria-selected="active === place.id"
            :tabindex="active === place.id ? 0 : -1"
            @click="active = place.id"
          >
            <Clock v-if="place.icon === 'recent'" class="nxt-icon-md" aria-hidden="true" />
            <Cloud v-else-if="place.icon === 'drive'" class="nxt-icon-md" aria-hidden="true" />
            <Monitor v-else class="nxt-icon-md" aria-hidden="true" />
            <span>{{ place.label }}</span>
          </button>
        </div>
        <div
          class="nx-office-file-dialog__pane"
          role="tabpanel"
          :aria-label="activeSource?.label ?? recentLabel"
        >
          <template v-if="activeSource">
            <p class="nx-office-file-dialog__hint">{{ activeSource.hint }}</p>
            <NxButton
              variant="default"
              :autofocus="focusComputerAction"
              @click="pick(activeSource.id)"
            >
              {{ activeSource.actionLabel }}
            </NxButton>
          </template>
          <template v-else>
            <ul v-if="recent?.length" class="nx-office-file-dialog__recent">
              <li v-for="file in recent" :key="file.id">
                <button
                  type="button"
                  class="nx-office-file-dialog__file"
                  :aria-current="file.current ? 'true' : undefined"
                  :disabled="file.current"
                  @click="openRecent(file)"
                >
                  <FileText class="nxt-icon-md" aria-hidden="true" />
                  <span class="nx-office-file-dialog__file-name">{{ file.name }}</span>
                  <span v-if="file.detail" class="nx-office-file-dialog__file-detail">
                    {{ file.detail }}
                  </span>
                </button>
              </li>
            </ul>
            <p v-else class="nx-office-file-dialog__hint">{{ recentEmptyLabel }}</p>
          </template>
        </div>
      </div>
    </template>
  </NxModal>
</template>

<style scoped>
.nx-office-file-dialog {
  display: flex;
  width: min(calc(var(--nxt-size-unit) * 40), 80vw);
  min-height: calc(var(--nxt-size-unit) * 17);
  gap: var(--nxt-space-lg);
}
.nx-office-file-dialog__rail {
  flex: none;
  width: calc(var(--nxt-size-unit) * 12.5);
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  padding-inline-end: var(--nxt-space-lg);
  border-inline-end: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-office-file-dialog__place {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  border: 0;
  border-radius: var(--nxt-radius-md);
  background: transparent;
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  text-align: start;
  cursor: pointer;
}
.nx-office-file-dialog__place:hover {
  background: var(--nxt-surface-sunken-bg);
}
.nx-office-file-dialog__place.is-active {
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-office-file-dialog__pane {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--nxt-space-md);
}
.nx-office-file-dialog__hint {
  margin: 0;
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
  line-height: 1.5;
}
.nx-office-file-dialog__recent {
  list-style: none;
  margin: 0;
  padding: 0;
  /* The global type reset sizes every bare <ul>/<p> at 16px medium. */
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-regular);
  width: 100%;
  max-height: calc(var(--nxt-size-unit) * 22);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}
.nx-office-file-dialog__file {
  width: 100%;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: var(--nxt-space-sm);
  align-items: center;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg);
  font: inherit;
  text-align: start;
  cursor: pointer;
}
.nx-office-file-dialog__file > svg {
  grid-row: span 2;
  color: var(--nxt-brand-bg);
}
.nx-office-file-dialog__file:hover:not(:disabled) {
  background: var(--nxt-surface-sunken-bg);
}
.nx-office-file-dialog__file:disabled {
  cursor: default;
  opacity: 0.6;
}
.nx-office-file-dialog__file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-office-file-dialog__file-detail {
  grid-column: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
}
:is(.nx-office-file-dialog__place, .nx-office-file-dialog__file):focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}
@media (max-width: 600px) {
  .nx-office-file-dialog {
    flex-direction: column;
  }
  .nx-office-file-dialog__rail {
    width: auto;
    flex-direction: row;
    flex-wrap: wrap;
    padding: 0 0 var(--nxt-space-md);
    border-inline-end: 0;
    border-bottom: var(--nxt-hairline) solid var(--nxt-border-subtle);
  }
}
</style>
